import { useEffect, useRef } from 'react'

interface FontSizeRule {
  minWidth?: number
  maxWidth?: number
  fontSize: string
}

interface FontSizeConfig {
  rules?: FontSizeRule[]
  defaultFontSize?: string
}

/**
 * 自定义Hook：用于动态设置HTML根元素的响应式font-size
 * 支持配置化的断点规则
 *
 * @param config 配置对象
 * @example
 * useHtmlFontSize({
 *   rules: [
 *     { minWidth: 700, maxWidth: 800, fontSize: '12vw' },
 *   ],
 *   defaultFontSize: '16px' // 可选的默认值
 * })
 */
export const useHtmlFontSize = (config: FontSizeConfig) => {
  const configRef = useRef(config)

  useEffect(() => {
    let hasModified = false

    const clearModified = () => {
      if (hasModified) {
        document.documentElement.style.removeProperty('font-size')
      }
    }

    const handleResize = () => {
      const screenWidth = window.innerWidth
      let matchedRule: FontSizeRule | null = null

      // 查找匹配的规则
      for (const rule of configRef.current.rules ?? []) {
        const minWidthMatch =
          rule.minWidth === undefined || screenWidth >= rule.minWidth
        const maxWidthMatch =
          rule.maxWidth === undefined || screenWidth <= rule.maxWidth

        if (minWidthMatch && maxWidthMatch) {
          matchedRule = rule
          break
        }
      }

      // 应用匹配的 font-size 或默认值
      if (matchedRule) {
        document.documentElement.style.setProperty(
          'font-size',
          matchedRule.fontSize,
          'important'
        )
        hasModified = true
      } else if (configRef.current.defaultFontSize) {
        document.documentElement.style.setProperty(
          'font-size',
          configRef.current.defaultFontSize,
          'important'
        )
        hasModified = true
      } else {
        clearModified()
      }
    }

    // 初始化执行一次
    handleResize()

    // 添加窗口大小变化监听
    window.addEventListener('resize', handleResize)

    return () => {
      clearModified()
      window.removeEventListener('resize', handleResize)
    }
  }, [])
}

export default useHtmlFontSize
